import { createRouter, createWebHistory } from 'vue-router'
import { log } from '@/services/log'
import { usePrintersStore } from '@/stores/common/printers'
import { useBroadcastMessagesStore } from '@/stores/common/broadcast-messages'
import { usePermissionsStore } from '@/stores/common/permissions'
import { useUsersStore } from '@/stores/common/users'
import { useEnterprisesStore } from '@/stores/common/enterprises'
import { useRolesStore } from '@/stores/common/roles'
import { useCategoriesStore } from '@/stores/common/categories'
import { useSitesStore } from '@/stores/common/sites'
import { useReadZonesStore } from '@/stores/common/readzones'
import { useFloorsStore } from '@/stores/common/floors'
import { useLocationTagsStore } from '@/stores/common/location-tags'
import { useImpinjReaderConfigsStore } from '@/stores/common/impinj-reader-configs'
import { useNotificationConfigsStore } from '@/stores/common/notification-configs'
import { usePickListsStore } from '@/stores/common/picklist-values'
import { useZebraReaderConfigsStore } from '@/stores/common/zebra-reader-configs'
import { useStandardReaderConfigsStore } from '@/stores/common/standard-reader-configs'
import { useOngoingUserSessionsStore } from '@/stores/common/ongoing-user-sessions'
import { useReadersStore } from '@/stores/common/readers'
import { useItemsHierarchyStore } from '@/stores/common/items-hierarchy'
import { useMovementHistoryStore } from '@/stores/common/movement-history'
import { useRemoteErrorLoggerStore } from '@/stores/common/remote-error-logger'
import { useUserActivityStore } from '@/stores/common/reports/user-activity'
/*
This is super important that each of create*Route function are either calling the createAppRoute or the route itself is created using the
createAppRoute function. It is strictly FORBIDDEN to create route objects directly unless they are not using our listing / form utilities. 
*/
export function createAppRoute(comp, path, meta, name) {
  const uri = path.startsWith('/') ? path.substring(1) : path
  const compName = name ?? uri.replaceAll('/', '_')
  const routeMeta = { ...meta, uri }
  const route = {
    component: comp,
    path,
    name: compName,
    meta: routeMeta,
    props: { uri },
  }
  log('created application route - path', path, route)
  return route
}

export function createListingPageRoute(path, storeFn, auth = true) {
  return createAppRoute(() => import('@/components/listing/tabular-listing-page.vue'), path, {
    requiresAuth: auth,
    storeFn,
  })
}

export function createListingPageRoutes(path, storeFn, auth = true, form = true, listingPage = true) {
  const routes = []
  if (listingPage == true) {
    routes.push(createListingPageRoute(path, storeFn, auth))
  }
  if (form == true) {
    routes.push(
      createAppRoute(() => import('@/components/form/dynamic-form.vue'), path + '/mono', {
        requiresAuth: auth,
        storeFn,
      })
    )
  }
  return routes
}
const dashboard = createAppRoute(
  () => import('@/views/common/default-dashboard.vue'),
  '/dashboard',
  { requiresAuth: true },
  'default-dashboard'
)
dashboard.alias = '/'
const commonRoutes = [
  ...createListingPageRoutes('/printers', usePrintersStore, true, true, true),
  ...createListingPageRoutes('/broadcast-messages', useBroadcastMessagesStore, true, true, true),
  createAppRoute(
    () => import('@/views/common/users/ongoing-sessions.vue'),
    '/users/ongoing-sessions',
    { requiresAuth: true, storeFn: useOngoingUserSessionsStore },
    'ongoingSessionsListPage'
  ),
  createAppRoute(
    () => import('@/views/common/permissions/click-matrix.vue'),
    '/permissions',
    { requiresAuth: true, storeFn: usePermissionsStore },
    'permissionsListPage'
  ),
  createAppRoute(
    () => import('@/views/common/users/mono.vue'),
    '/users/mono',
    { requiresAuth: true, storeFn: useUsersStore },
    'usersMonoPage'
  ),
  createAppRoute(
    () => import('@/views/common/users/index.vue'),
    '/users',
    { requiresAuth: true, storeFn: useUsersStore },
    'usersListingPage'
  ),
  ...createListingPageRoutes('/enterprises', useEnterprisesStore, true, true, true),
  ...createListingPageRoutes('/roles', useRolesStore, true, true, true),
  ...createListingPageRoutes('/categories', useCategoriesStore, true, true, true),
  ...createListingPageRoutes('/sites', useSitesStore, true, true, true),
  createAppRoute(() => import('@/views/common/reports/consists-report.vue'), '/reports/consists-report', {
    requiresAuth: true,
    storeFn: useItemsHierarchyStore,
  }),
  createAppRoute(
    () => import('@/views/common/readzones/index.vue'),
    '/readzones',
    {
      requiresAuth: true,
      storeFn: useReadZonesStore,
    },
    'readzonesListingPage'
  ),
  createAppRoute(
    () => import('@/views/common/picklists-values/picklists-listing-page.vue'),
    '/picklist-values',
    {
      requiresAuth: true,
      storeFn: usePickListsStore,
    },
    'picklistsListingPage'
  ),
  createAppRoute(
    () => import('@/views/common/readzones/mono.vue'),
    '/readzones/mono',
    { requiresAuth: true, storeFn: useReadZonesStore },
    'readzonesMonoPage'
  ),
  createListingPageRoute('/floors', useFloorsStore, true),
  createAppRoute(
    () => import('@/views/common/location-map-mono.vue'),
    '/floors/mono',
    { requiresAuth: true, storeFn: useFloorsStore },
    'LocationMapForm'
  ),
  createAppRoute(
    () => import('@/views/common/readers/readers-listing-page.vue'),
    '/readers',
    { requiresAuth: true, storeFn: useReadersStore },
    'readersListingPage'
  ),
  createAppRoute(
    () => import('@/views/common/readers/readers-mono.vue'),
    '/readers/mono',
    { requiresAuth: true, storeFn: useReadersStore },
    'readersForm'
  ),
  createAppRoute(
    () => import('@/views/common/readers/configs/impinj/mono.vue'),
    '/readers/configs/impinj/mono',
    { requiresAuth: true, storeFn: useImpinjReaderConfigsStore },
    'impinjForm'
  ),
  createAppRoute(
    () => import('@/views/common/readers/configs/zebra/mono.vue'),
    '/readers/configs/zebra/mono',
    { requiresAuth: true, storeFn: useZebraReaderConfigsStore },
    'zebraForm'
  ),
  createAppRoute(
    () => import('@/views/common/readers/configs/standard/mono.vue'),
    '/readers/configs/standard/mono',
    { requiresAuth: true, storeFn: useStandardReaderConfigsStore },
    'standardForm'
  ),
  ...createListingPageRoutes('/location-tags', useLocationTagsStore, true, true, true),
  createAppRoute(
    () => import('@/views/common/readers/configs/impinj/index.vue'),
    '/readers/configs/impinj',
    {
      requiresAuth: true,
      storeFn: useImpinjReaderConfigsStore,
    },
    'impinjReaderConfig'
  ),
  createAppRoute(
    () => import('@/views/common/readers/configs/zebra/index.vue'),
    '/readers/configs/zebra',
    {
      requiresAuth: true,
      storeFn: useZebraReaderConfigsStore,
    },
    'zebraReaderConfig'
  ),
  createAppRoute(
    () => import('@/views/common/readers/configs/standard/index.vue'),
    '/readers/configs/standard',
    {
      requiresAuth: true,
      storeFn: useStandardReaderConfigsStore,
    },
    'standardReaderConfig'
  ),
  createAppRoute(
    () => import('@/views/common/notification-configs/mono.vue'),
    '/notification-configs/mono',
    { requiresAuth: true, storeFn: useNotificationConfigsStore },
    'notificationConfigForm'
  ),
  createAppRoute(
    () => import('@/views/common/movement-history/index.vue'),
    '/movement-history',
    { requiresAuth: true, storeFn: useMovementHistoryStore },
    'movementHistory'
  ),
  createAppRoute(
    () => import('@/views/common/remote-error-logger/index.vue'),
    '/remote-error-logger',
    { requiresAuth: true, storeFn: useRemoteErrorLoggerStore },
    'remoteErrorListingPage'
  ),
  createAppRoute(
    () => import('@/views/common/rfid-debug-support/index.vue'),
    '/rfid-debug-support',
    { requiresAuth: true },
    'debugSupport'
  ),
  createAppRoute(
    () => import('@/views/common/reports/user-activity.vue'),
    '/reports/user-activity',
    { requiresAuth: true, storeFn: useUserActivityStore },
    'userActivityReport'
  ),
  createListingPageRoute('/notification-configs', useNotificationConfigsStore, true),
  createAppRoute(() => import('@/components/UserLogin.vue'), '/login', { requiresAuth: false }, 'user-login'),
  dashboard,
]

export function createAppRouter(routes) {
  const base = '/'
  log('While creating router, using base', base, import.meta.env.DEV)
  const merged = {
    history: createWebHistory(base),
    routes: [...commonRoutes, ...routes],
  }
  const router = createRouter(merged)
  const beforeGuardGlobs = import.meta.glob('./guards/*.before.js', { eager: true })
  Object.values(beforeGuardGlobs).forEach((module) => router.beforeEach(module.default))
  const afterGuardGlobs = import.meta.glob('./guards/*.after.js', { eager: true })
  Object.values(afterGuardGlobs).forEach((module) => router.afterEach(module.default))
  return router
}
